import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, of, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuctionBidQuery } from '../../loyalty-history/auction-bid-details/auction-bid.query';
import { AuctionBidService } from '../../loyalty-history/auction-bid-details/auction-bid.service';
import { PurchaseQuery } from '../../loyalty-history/purchase/purchase.query';
import { AuctionQuery } from '../auction/auction.query';
import { AuctionService } from '../auction/auction.service';
import { LottoWalletQuery } from '../lotto/lotto-wallet.query';
import { LottoQuery } from '../lotto/lotto.query';
import { LottoService } from '../lotto/lotto.service';
import { MarketplaceExtendedQuery } from '../marketplace/marketplace.extended.query';
import { MarketplaceService } from '../marketplace/marketplace.service';
import { ProcessProviderId } from '../marketplace/models/process-provider-id';
import { MixedLoyaltyItemType } from './models/mixed-loyalty-item-type.enum';
import { MixedLoyaltyItem } from './models/mixed-loyalty-item.model';
import { MixedLoyaltyWalletItem } from './models/mixed-loyalty-wallet-item.model';
import { PurchaseService } from '../../loyalty-history/purchase/purchase.service';
import { LoyaltyStatusService } from './loyalty-status.service';
import { LOTTO_SOURCE, MARKETPLACE_SOURCE } from './sources/loyalty-source.model';

@Injectable({
  providedIn: 'root',
})
export class MixedLoyaltyQuery {
  // Workaround fix for favorite loaded observable
  protected favoriteLoadedSubject = new BehaviorSubject<boolean>(false);
  favoriteLoaded$ = this.favoriteLoadedSubject.asObservable();

  protected auctionQuery = inject(AuctionQuery);
  protected marketplaceExtendedQuery = inject(MarketplaceExtendedQuery);
  protected lottoQuery = inject(LottoQuery);
  protected purchaseQuery = inject(PurchaseQuery);
  protected auctionBidQuery = inject(AuctionBidQuery);
  protected auctionBidService = inject(AuctionBidService);
  protected lottoWalletQuery = inject(LottoWalletQuery);
  protected auctionService = inject(AuctionService);
  protected lottoService = inject(LottoService);
  protected marketplaceService = inject(MarketplaceService);
  protected purchaseService = inject(PurchaseService);
  protected statusService = inject(LoyaltyStatusService);

  /**
   * The marketplace and lotto data sources in use, each selected independently by its own
   * `componentVersions` entry (see `provideVersionedService`). Composing sources rather than
   * subclassing this query keeps every version combination expressible with one implementation.
   */
  protected marketplaceSource = inject(MARKETPLACE_SOURCE);
  protected lottoSource = inject(LOTTO_SOURCE);

  /**
   * selects a combination of loyalty items, optionally filtered
   * @param itemType - the type of loyalty item to show
   * @param marketplaceFilterTypes - which marketplace item types to show
   */
  public selectMixedLoyaltyItems(
    itemTypes: MixedLoyaltyItemType[] = [MixedLoyaltyItemType.All],
    marketplaceFilterTypes: ProcessProviderId[] = [0, 1, 2, 3, 4, 5]
  ) {
    const auctions$ = this.auctionQuery.selectAuctions().pipe(
      map((auctions) => {
        return itemTypes.includes(MixedLoyaltyItemType.Auction) || itemTypes.includes(MixedLoyaltyItemType.All)
          ? auctions.map((x) => new MixedLoyaltyItem({ type: 'auction', data: x, status: this.statusService.getStatus(x) }))
          : ([] as MixedLoyaltyItem[]);
      })
    );

    const marketplaceItems$ = this._includes(itemTypes, MixedLoyaltyItemType.Marketplace)
      ? this.marketplaceSource.selectAll(marketplaceFilterTypes)
      : of([] as MixedLoyaltyItem[]);

    const lottos$ = this._includes(itemTypes, MixedLoyaltyItemType.Lotto)
      ? this.lottoSource.selectAll(marketplaceFilterTypes)
      : of([] as MixedLoyaltyItem[]);

    // Combines the lists by alternating i.e. - [[1,2,3],[4,5,6]] becomes [1,4,2,5,3,6]
    let combined$ = combineLatest([auctions$, marketplaceItems$, lottos$]).pipe(
      map((arrays) =>
        arrays.reduce((t, u, v, w) => u.reduce((x, y, z) => ((x[z * w.length + v] = y), x), t), []).filter((e) => e != null)
      )
    );

    return combined$;
  }

  /**
   * selected a combination of loyalty items that the user has purchase, optionally filtered
   * @param onlyRedeemed - show only redeemed items
   * @param marketplaceFilterTypes - which marketplace item types to show
   * @param itemType - which loyalty item type to show
   */
  public selectMixedLoyaltyWalletItems(
    onlyRedeemed: boolean,
    marketplaceFilterTypes: ProcessProviderId[] = [0, 1, 2, 3, 4, 5],
    itemTypes: MixedLoyaltyItemType[] = [MixedLoyaltyItemType.All]
  ) {
    zip(
      this.auctionService.fetchAuctions(),
      this.lottoSource.fetch(),
      this.marketplaceSource.fetch(),
      this.auctionBidService.fetchBids(),
      this.purchaseService.fetchMemberPurchases()
    ).toPromise();

    return this.selectWalletItems(onlyRedeemed, marketplaceFilterTypes, itemTypes);
  }

  /**
   * selected a combination of loyalty items that the user has purchase, optionally filtered
   * @param onlyRedeemed - show only redeemed items
   * @param marketplaceFilterTypes - which marketplace item types to show
   * @param itemType - which loyalty item type to show
   */
  public selectMixedLoyaltyWalletItemsNoFetch(
    onlyRedeemed: boolean,
    marketplaceFilterTypes: ProcessProviderId[] = [0, 1, 2, 3, 4, 5],
    itemTypes: MixedLoyaltyItemType[] = [MixedLoyaltyItemType.All]
  ) {
    return this.selectWalletItems(onlyRedeemed, marketplaceFilterTypes, itemTypes);
  }

  /**
   * Selects a combination of favorite loyalty items, optionally filtered
   * @param showSoldOut - show sold out items or not
   * @param itemTypes - item types to show
   */
  public selectFavouriteMixedLoyaltyItems(
    showSoldOut: boolean = true,
    itemTypes: MixedLoyaltyItemType[] = [MixedLoyaltyItemType.All]
  ) {
    zip(this.auctionService.fetchAuctions(), this.lottoSource.fetch(), this.marketplaceSource.fetch())
      .toPromise()
      .finally(() => {
        this.favoriteLoadedSubject.next(true);
      });

    const auctions$ = this.auctionQuery.selectFavourites().pipe(
      map((auctions) => {
        return itemTypes.includes(MixedLoyaltyItemType.Auction) || itemTypes.includes(MixedLoyaltyItemType.All)
          ? auctions.map((x) => new MixedLoyaltyItem({ type: 'auction', data: x, status: this.statusService.getStatus(x) })) // ({ type: 'auction', data: x, status: this.statusService.getStatus(x) }
          : ([] as MixedLoyaltyItem[]);
      }),
      map((auctions) => auctions.sort(this._sortByOrdinal))
    );

    const marketplaceItems$ = (
      this._includes(itemTypes, MixedLoyaltyItemType.Marketplace)
        ? this.marketplaceSource.selectFavourites(showSoldOut)
        : of([] as MixedLoyaltyItem[])
    ).pipe(map((marketplaceItems) => marketplaceItems.sort(this._sortByOrdinal)));

    const lottos$ = (
      this._includes(itemTypes, MixedLoyaltyItemType.Lotto)
        ? this.lottoSource.selectFavourites(showSoldOut)
        : of([] as MixedLoyaltyItem[])
    ).pipe(map((lottos) => lottos.sort(this._sortByOrdinal)));

    // Combines the lists by alternating i.e. - [[1,2,3],[4,5,6]] becomes [1,4,2,5,3,6]
    let combined$ = zip(auctions$, marketplaceItems$, lottos$).pipe(
      map((arrays) =>
        arrays.reduce((t, u, v, w) => u.reduce((x, y, z) => ((x[z * w.length + v] = y), x), t), []).filter((e) => e != null)
      )
    );

    return combined$;
  }

  public async walletFetch() {
    await forkJoin([
      this.auctionService.fetchAuctions(),
      this.lottoSource.fetch(),
      this.marketplaceSource.fetch(),
      this.auctionBidService.fetchBids(),
      this.purchaseService.fetchMemberPurchases(),
    ]).toPromise();
  }

  /** Whether a requested item type should be included (explicitly, or via `All`). */
  protected _includes(itemTypes: MixedLoyaltyItemType[], type: MixedLoyaltyItemType): boolean {
    return itemTypes.includes(type) || itemTypes.includes(MixedLoyaltyItemType.All);
  }

  // Sort function for sorting items by ordinal
  protected _sortByOrdinal(a: MixedLoyaltyItem, b: MixedLoyaltyItem) {
    if (a.data.Ordinal < b.data.Ordinal) {
      return -1;
    } else if (a.data.Ordinal > b.data.Ordinal) {
      return 1;
    }
    return 0;
  }

  private selectWalletItems(
    onlyRedeemed: boolean,
    marketplaceFilterTypes: ProcessProviderId[] = [0, 1, 2, 3, 4, 5],
    itemTypes: MixedLoyaltyItemType[] = [MixedLoyaltyItemType.All]
  ) {
    const auctions$ = this.auctionBidQuery.selectWonBids().pipe(
      map((auctions) => {
        return itemTypes.includes(MixedLoyaltyItemType.Auction) || itemTypes.includes(MixedLoyaltyItemType.All)
          ? auctions
              .filter((auction) => auction.RedeemStatus == +onlyRedeemed)
              .map((x) => ({ type: 'auction', data: x }) as MixedLoyaltyWalletItem)
          : ([] as MixedLoyaltyWalletItem[]);
      })
    );

    const marketplaceItems$ = this.purchaseQuery.selectPurchases().pipe(
      map((marketplacePurchases) => {
        return itemTypes.includes(MixedLoyaltyItemType.Marketplace) || itemTypes.includes(MixedLoyaltyItemType.All)
          ? marketplacePurchases
              .filter((item) => marketplaceFilterTypes.includes(item.ProcessProviderId) && item.RedeemStatus == +onlyRedeemed)
              .map((x) => ({ type: 'marketplace', data: x }) as MixedLoyaltyWalletItem)
          : ([] as MixedLoyaltyWalletItem[]);
      })
    );

    const lottos$ = this.lottoWalletQuery.selectLottoWalletItems().pipe(
      map((lottos) => {
        if ((itemTypes.includes(MixedLoyaltyItemType.Lotto) || itemTypes.includes(MixedLoyaltyItemType.All)) && onlyRedeemed) {
          return lottos.map((x) => ({ type: 'lotto', data: x }) as MixedLoyaltyWalletItem);
        }
        return [] as MixedLoyaltyWalletItem[];
      })
    );

    // Combines the lists by alternating i.e. - [[1,2,3],[4,5,6]] becomes [1,4,2,5,3,6]
    let combined$ = zip(auctions$, marketplaceItems$, lottos$).pipe(
      map((arrays) =>
        arrays.reduce((t, u, v, w) => u.reduce((x, y, z) => ((x[z * w.length + v] = y), x), t), []).filter((e) => e != null)
      )
    );

    return combined$;
  }
}
